import { defineStore } from 'pinia'
import api from '@/services/api'
import { JWTToken, KeyAndPasswordVM, LoginVM } from '@/models'
import { AxiosResponse } from 'axios'
import router from '@/router'
import config from '@/helpers/config'
import { useAccountStore } from './account.store'
import KycService from '@/services/kyc.service'

export const useAuthStore = defineStore({
  id: 'auth',
  state: () => ({
    token: {
      idToken: JSON.parse(localStorage.getItem('token') as string)
    } as JWTToken,
    returnUrl: config.returnUrl,
    accountStore: useAccountStore()
  }),
  actions: {
    authenticate: async function (payload: LoginVM): Promise<AxiosResponse<any>> {
      return await api.post('/api/authenticate', JSON.stringify(payload))
    },

    getUserData: async function () {
      return await api.get('/api/account')
    },

    forgotPasswordInit: async function (email: string) {
      return await api.patch(`/api/account/reset-password/init/${email}`)
    },

    forgotPasswordFinit: async function (payload: KeyAndPasswordVM) {
      return await api.post('/api/account/reset-password/finish', JSON.stringify(payload))
    },

    updatePassword: async function (currentPassword: string, newPassword: string) {
      return await api.post('/api/account/change-password', {
        currentPassword,
        newPassword
      })
    },

    setToken: function (token: JWTToken) {
      this.token = token
      localStorage.setItem('token', JSON.stringify(this.token.idToken))
    },

    setReturnUrl: function (returnUrl: string) {
      this.returnUrl = returnUrl
    },

    /**
     * Check KYC status and redirect to verification page if needed
     * Returns true if user should be redirected to KYC verification
     */
    checkKycStatusAndRedirect: async function (): Promise<boolean> {
      try {
        // Récupérer les données utilisateur actuelles
        await this.accountStore.refreshData()

        const clientId = this.accountStore.accountData.address?.client?.id

        if (!clientId) {
          console.log('No client ID found, skipping KYC check')
          return false
        }

        // Déterminer si on doit créer une nouvelle session
        // Créer une session si le statut est NOT_VERIFIED
        const shouldCreateSession = this.accountStore.accountData.kycVerificationStatus === 'NOT_VERIFIED'

        // Récupérer les informations KYC depuis le backend
        const kycInfo = await KycService.getClientKycIntegrationInfo(clientId, shouldCreateSession)

        console.log('KYC Info:', kycInfo)
        console.log('Latest Verification Status:', kycInfo.latestVerificationStatus)
        console.log('Can Start New Verification:', kycInfo.canStartNewVerification)
        console.log('Verification Required:', kycInfo.verificationRequired)

        // Vérifier si l'utilisateur a besoin de faire la vérification KYC
        // Rediriger si:
        // 1. La vérification est requise (verificationRequired = true) OU
        // 2. Le statut est NOT_VERIFIED, DECLINED, ou PENDING (vérification incomplète)
        const needsVerification = kycInfo.verificationRequired === true ||
          ['NOT_VERIFIED', 'DECLINED', 'PENDING', 'SUBMITTED', 'RESUBMISSION_REQUESTED'].includes(
            kycInfo.latestVerificationStatus || ''
          )

        if (needsVerification) {
          console.log('User needs KYC verification, redirecting to KYC Verification page')
          
          // Rediriger vers la page de vérification KYC
          // Utiliser setTimeout pour s'assurer que la redirection ne soit pas écrasée
          setTimeout(() => {
            router.push({ name: 'KycVerification' }).catch(err => {
              console.error('Erreur de redirection:', err)
            })
          }, 100)
          return true
        }

        console.log('User KYC is verified, skipping KYC verification')
        return false
      } catch (error) {
        console.error('Erreur lors de la vérification du statut KYC:', error)
        return false
      }
    },

    logout: function (expired = '') {
      this.token = {} as JWTToken
      config.isLoggedIn = false
      router.push({
        name: 'Login',
        params: { returnUrl: this.returnUrl, username: this.accountStore.accountData.login, expired }
      })
      localStorage.clear()
    }
  }
})
